/*
 * ─── HUB LANDING PAGE ────────────────────────────────────────────────────────
 * DONUT REVERT INSTRUCTIONS:
 * To revert to the original flat sidebar + dashboard:
 *   1. In App.tsx: Change `<Route path="/" component={HubPage} />` back to
 *      `<Route path="/" component={DashboardPage} />`
 *   2. In App.tsx: Remove the /hub, /cfo, /coo, /dealership routes
 *   3. In app-sidebar.tsx: Remove the `section` prop and restore original navItems array
 *
 * ORIGINAL navItems array (preserved for DONUT revert):
 * const navItems = [
 *   { title: "Dashboard", url: "/", icon: LayoutDashboard },
 *   { title: "Floorplan Analytics", url: "/floorplan", icon: CreditCard },
 *   { title: "Inventory Analysis", url: "/inventory", icon: Layers },
 *   { title: "SLA & Goals", url: "/sla", icon: Target },
 *   { title: "Dealership Analysis", url: "/operations", icon: TrendingUp },
 *   { title: "Documents", url: "/documents", icon: FolderOpen },
 *   { title: "Create a Financial Statement", url: "/financial-statements", icon: FileSpreadsheet },
 *   { title: "Vendor Directory", url: "/vendor-directory", icon: Store },
 *   { title: "Dean Chats", url: "/dean", icon: Sparkles },
 * ];
 *
 * ORIGINAL routes in App.tsx Router() (preserved for DONUT revert):
 * <Route path="/" component={DashboardPage} />
 * <Route path="/floorplan" component={FloorplanPage} />
 * <Route path="/inventory" component={FloorplanInventoryPage} />
 * <Route path="/operations" component={OperationsPage} />
 * <Route path="/goals">{() => <Redirect to="/sla" />}</Route>
 * <Route path="/sla" component={SlaPage} />
 * <Route path="/ai">{() => <Redirect to="/" />}</Route>
 * <Route path="/account" component={AccountPage} />
 * <Route path="/vendor-directory" component={VendorDirectoryPage} />
 * <Route path="/bank-health" component={BankHealthPage} />
 * <Route path="/documents" component={DocumentsPage} />
 * <Route path="/financial-statements" component={FinancialStatementsPage} />
 * <Route path="/dean" component={DeanChatsPage} />
 * <Route path="/admin/ai-intelligence">{() => <Redirect to="/admin" />}</Route>
 */

import { useLocation } from "wouter";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { DollarSign, Settings2, BarChart3, ArrowRight } from "lucide-react";
import type { DealerProfile } from "@shared/schema";

interface HubCardProps {
  title: string;
  subtitle: string;
  description: string;
  icon: React.ReactNode;
  accentColor: string;
  glowColor: string;
  borderColor: string;
  href: string;
  testId: string;
}

function HubCard({
  title,
  subtitle,
  description,
  icon,
  accentColor,
  glowColor,
  borderColor,
  href,
  testId,
}: HubCardProps) {
  const [, navigate] = useLocation();
  const { t } = useTranslation(["nav", "common"]);

  return (
    <button
      type="button"
      data-testid={testId}
      onClick={() => navigate(href)}
      className={`
        group relative flex flex-col gap-5 rounded-2xl border p-8 text-left
        bg-black/60 backdrop-blur-sm
        transition-all duration-300 ease-out
        hover:scale-[1.02] hover:-translate-y-1
        focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/30
        ${borderColor}
        ${glowColor}
      `}
      style={{
        boxShadow: "0 0 0 1px rgba(255,255,255,0.05)",
      }}
    >
      <div
        className={`
          w-14 h-14 rounded-xl flex items-center justify-center shrink-0
          transition-transform duration-300 group-hover:scale-110
          ${accentColor}
        `}
      >
        {icon}
      </div>

      <div className="flex-1 space-y-2">
        <div>
          <p className="text-xs font-semibold uppercase tracking-widest text-white/40 mb-1">
            {subtitle}
          </p>
          <h2 className="text-2xl font-bold text-white leading-tight">{title}</h2>
        </div>
        <p className="text-sm text-white/50 leading-relaxed">{description}</p>
      </div>

      <div className="flex items-center gap-2 text-sm font-medium text-white/60 group-hover:text-white/90 transition-colors">
        {t("nav:hub.enterSection")}
        <ArrowRight className="w-4 h-4 transition-transform duration-200 group-hover:translate-x-1" />
      </div>

      <div
        className="absolute inset-0 rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse at 50% 0%, rgba(255,255,255,0.04) 0%, transparent 70%)",
        }}
      />
    </button>
  );
}

export default function HubPage() {
  const [, navigate] = useLocation();
  const { t } = useTranslation(["nav", "common"]);

  const { data: profile } = useQuery<DealerProfile | null>({
    queryKey: ["/api/dealer-profile"],
    retry: false,
    staleTime: 60_000,
  });

  const dealershipName = profile?.dealershipName;

  return (
    <div className="min-h-full bg-black flex flex-col items-center justify-center px-4 py-12 relative overflow-hidden">
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(ellipse 80% 50% at 50% -10%, rgba(99,102,241,0.12) 0%, transparent 60%)",
        }}
      />

      <div className="relative z-10 w-full max-w-5xl space-y-12">
        <div className="text-center space-y-3">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-white/10 bg-white/5 text-xs text-white/50 font-mono uppercase tracking-widest mb-2">
            <BarChart3 className="w-3 h-3" />
            {t("nav:hub.badge")}
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold text-white tracking-tight">
            {dealershipName ? t("nav:hub.commandHubNamed", { name: dealershipName }) : t("nav:hub.commandHub")}
          </h1>
          <p className="text-white/40 text-base max-w-md mx-auto">
            {t("nav:hub.subtitle")}
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <HubCard
            testId="hub-card-cfo"
            href="/cfo"
            title={t("nav:cfoDean")}
            subtitle={t("nav:hub.cfoSubtitle")}
            description={t("nav:hub.cfoDesc")}
            icon={<DollarSign className="w-7 h-7 text-white" />}
            accentColor="bg-emerald-500/20 border border-emerald-500/30"
            glowColor="hover:shadow-[0_0_40px_rgba(16,185,129,0.15)]"
            borderColor="border-white/8 hover:border-emerald-500/40"
          />

          <HubCard
            testId="hub-card-coo"
            href="/coo"
            title={t("nav:cooDean")}
            subtitle={t("nav:hub.cooSubtitle")}
            description={t("nav:hub.cooDesc")}
            icon={<Settings2 className="w-7 h-7 text-white" />}
            accentColor="bg-blue-500/20 border border-blue-500/30"
            glowColor="hover:shadow-[0_0_40px_rgba(59,130,246,0.15)]"
            borderColor="border-white/8 hover:border-blue-500/40"
          />

          <HubCard
            testId="hub-card-dealership"
            href="/dealership"
            title={t("nav:dealershipAtAGlance")}
            subtitle={t("nav:hub.dealershipSubtitle")}
            description={t("nav:hub.dealershipDesc")}
            icon={<BarChart3 className="w-7 h-7 text-white" />}
            accentColor="bg-violet-500/20 border border-violet-500/30"
            glowColor="hover:shadow-[0_0_40px_rgba(139,92,246,0.15)]"
            borderColor="border-white/8 hover:border-violet-500/40"
          />
        </div>

      </div>
    </div>
  );
}
